import { CheckedIconWithoutBg, SquareDownArrow, SquareUpArrow } from 'assests/GmsIcons';
import React, { useMemo, useState } from 'react'
import { attributeActions } from '../utils/Constants';
import CommonAttributeValues from './CommonAttributeValues';
import UniqueAttributeValues from './UniqueAttributeValues';
import NoDataComponent from 'Components/Common/NoDataComponent';
import { getCommonAttributeValues } from '../services/RecategorizeServices';
import Select, { components } from "react-select";
import { customStyles } from '../utils/Styles';
import Table from 'Components/DesignSystem/Table/Table';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import { TableColumn } from 'types/DesignSystem/Table';

type SourceUniqueAttributesTableProps = {
    payload: TRecategorizePayload;
    setPayload: React.Dispatch<React.SetStateAction<TRecategorizePayload>>;
    recategorizationResponse: RecategorizationResponse | undefined;
    setAttributeValueData: React.Dispatch<React.SetStateAction<AttributeValueMapping | null>>;
}

const SourceUniqueAttributesTable = ({ payload, setPayload, recategorizationResponse, setAttributeValueData }: SourceUniqueAttributesTableProps) => {
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set(['variant']));
    const [checkedRows, setCheckedRows] = useState<Set<string>>(new Set(payload.unique_attr_source_list.filter(row => row?.checked).map(row => row?.attribute_id)));

    const getCommonValues = (sourceAttributeId: string) => {
        const sourceAttr = payload.unique_attr_source_list.find(
            attr => attr.attribute_id === sourceAttributeId
        );
        return sourceAttr?.common_attribute_values ?? [];
    };

    const getUniqueAttributeValues = (
        sourceAttrValues: AttributeValue[],
        commonValues: string[]
    ): AttributeValue[] => {
        return sourceAttrValues.filter(item => !commonValues.includes(item.value));
    };

    const commonAttributeValues = (sourceAttributeId: string): string[] => {
        const sourceAttr = payload?.unique_attr_source_list?.find(attr => attr.attribute_id === sourceAttributeId);
        return sourceAttr?.common_attribute_values ?? [];
    };

    const sourceAttributeValues = (sourceAttributeId: string): AttributeValue[] => {
        return recategorizationResponse?.unique_attr_source_list?.filter((e) => e.attribute_id === sourceAttributeId)[0]?.attribute_value ?? [];
    }

    const toggleExpand = (id: string): void => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const toggleCheck = (id: string): void => {
        setCheckedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });

        if (recategorizationResponse?.unique_attr_source_list) {
            const updatedAttrList = recategorizationResponse.unique_attr_source_list.map(attr => {
                const existingAttr = payload.unique_attr_source_list.find(
                    existing => existing.attribute_id === attr.attribute_id
                );

                const isClickedItem = attr.attribute_id === id;
                const isUnchecking = checkedRows.has(id);

                return {
                    ...existingAttr,
                    attribute_id: attr.attribute_id,
                    attribute_name: attr.attribute_name,
                    checked: isClickedItem ? !isUnchecking : checkedRows.has(attr.attribute_id),
                    action: isClickedItem && isUnchecking ? null : (existingAttr?.action),
                    target_attribute_id: isClickedItem && isUnchecking ? undefined : existingAttr?.target_attribute_id,
                    target_attribute_name: isClickedItem && isUnchecking ? undefined : existingAttr?.target_attribute_name,
                } as UniqueAttributePayload;
            });

            setPayload(prev => ({
                ...prev,
                unique_attr_source_list: updatedAttrList
            }));
        }
    };

    const handleActionChange = (attributeId: string, action: AttributeActionType) => {
        setPayload(prev => {
            const updatedUniqueAttrList = prev.unique_attr_source_list.map(attr =>
                attr.attribute_id === attributeId
                    ? {
                        ...attr,
                        action,
                        target_attribute_id: undefined,
                        target_attribute_name: undefined,
                        unique_attribute_values: [],
                        common_attribute_values: []
                    }
                    : attr
            );

            return {
                ...prev,
                unique_attr_source_list: updatedUniqueAttrList
            };
        });
    };

    const handleTargetAttributeChange = async (
        attributeId: string,
        targetAttributeId: string,
        targetAttributeName: string
    ) => {
        const sourceAttrValues = sourceAttributeValues(attributeId);
        const commonAttributeValuesResponse = await fetchCommonAttributeValues(
            targetAttributeId,
            sourceAttrValues
        );
        const commonValues = commonAttributeValuesResponse?.map((e: AttributeValue) => e.value) || [];

        setPayload(prev => {
            const updatedUniqueAttrList = prev.unique_attr_source_list.map(attr => {
                if (attr.attribute_id !== attributeId) return attr;

                const uniqueValues = getUniqueAttributeValues(sourceAttrValues, commonValues)
                    .map(val => ({
                        attribute_value: val.value,
                        action: undefined
                    }));

                return {
                    ...attr,
                    target_attribute_id: targetAttributeId,
                    target_attribute_name: targetAttributeName,
                    unique_attribute_values: uniqueValues,
                    common_attribute_values: commonValues
                };
            });

            return {
                ...prev,
                unique_attr_source_list: updatedUniqueAttrList
            };
        });
    };

    const fetchCommonAttributeValues = async (targetAttributeId: string, attributeValueList: AttributeValue[]) => {
        const attributeValues = attributeValueList.map(attrValue => attrValue.value);
        const formattedAttributeValues = JSON.stringify(attributeValues);
        try {
            const response = await getCommonAttributeValues(targetAttributeId, formattedAttributeValues);
            return response.data?.common_attribute_val ?? [];
        } catch (error) {
            console.error("Error fetching common attribute values:", error);
        }

    }

    const targetAttributeOptions = (
        currentAttributeId: string,
        targetUniqueAttributeList: Attribute[],
        existingSelection: UniqueAttributePayload[]
    ) => {
        const compareIds = new Set(
            existingSelection
                .filter(item => item.attribute_id !== currentAttributeId)
                .map(item => item.target_attribute_id)
        )

        return targetUniqueAttributeList
            .filter(attr => !compareIds.has(attr.attribute_id))
            .map(attr => ({
                value: attr.attribute_id,
                label: attr.attribute_name
            }))
    }

    const actionTypeSelectProps = useMemo(() => ({
        id: "actionTypeId",
        className: "tw-outline-none tw-w-full",
        classNamePrefix: "select",
        styles: {
            ...customStyles,
            loadingIndicator: (base: any) => ({
                ...base,
                color: 'var(--ds-accent, #030562)'
            })
        },
        isSearchable: false,
        maxMenuHeight: 220,
        menuPlacement: "auto" as const,
        menuPosition: "absolute" as const,
        // Escapes the DS Table wrapper's overflow box — see utils/Styles.tsx `menuPortal`.
        menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
        isClearable: false,
        placeholder: "Select"
    }), []);

    const selectProps = useMemo(() => ({
        id: "attributeId",
        className: "tw-outline-none tw-w-full",
        classNamePrefix: "select",
        styles: {
            ...customStyles,
            loadingIndicator: (base: any) => ({
                ...base,
                color: 'var(--ds-accent, #030562)'
            })
        },
        isSearchable: true,
        maxMenuHeight: 220,
        menuPlacement: "auto" as const,
        menuPosition: "absolute" as const,
        // Escapes the DS Table wrapper's overflow box — see utils/Styles.tsx `menuPortal`.
        menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
        isClearable: false,
        placeholder: "Select Attribute"
    }), []);

    if (!payload?.unique_attr_source_list || payload.unique_attr_source_list.length === 0) {
        return <NoDataComponent />
    }

    /*
     * The grid moved to DS `Table` (2026-09-13). Every handler above is verbatim —
     * `toggleCheck` still reads the PRE-update `checkedRows` to decide the new flag and
     * still silently no-ops when `recategorizationResponse.unique_attr_source_list` is
     * missing (a divergence this file's spec pins deliberately), and
     * `handleTargetAttributeChange` still fetches common values before writing.
     *
     * `isRowExpandable` is the one behavioural nuance: the hand-rolled table drew a
     * chevron on EVERY row, but rendered detail only for `transfer` and `map`, so a row
     * with no action yet had a control that opened nothing. The DS asks which rows can
     * open (§ 69's rule against focusable no-ops), so those rows now get an empty expand
     * cell instead of a dead button. No payload or selection behaviour changes.
     */
    const columns = useMemo<TableColumn<UniqueAttributePayload>[]>(() => [
        {
            id: 'selected',
            header: '',
            accessor: 'checked',
            width: '10%',
            cellType: 'custom',
            render: (_v, row) => (
                <Checkbox
                    checked={checkedRows.has(row.attribute_id)}
                    onChange={() => toggleCheck(row.attribute_id)}
                    data-testid={`recategorize-source-row-${row.attribute_id}`}
                    aria-label={row.attribute_name ?? row.attribute_id}
                />
            ),
        },
        {
            id: 'source',
            header: payload?.source_category_name ?? '',
            accessor: 'attribute_name',
            width: '30%',
        },
        {
            id: 'action',
            header: 'Action',
            accessor: 'action',
            width: '24%',
            cellType: 'dropdown',
            render: (_v, row) => (
                /*
                 * Two enumerable options — the DS's own rule sends that to `Dropdown`,
                 * not `Combobox` ("reach for Dropdown when the list is short and
                 * enumerable"). `usePortal`, for the same reason the react-selects are
                 * portalled: this cell sits inside the DS Table's overflow box.
                 *
                 * `handleActionChange` is untouched; only the shape reaching it changed
                 * — react-select handed over an OPTION, Dropdown hands over the value.
                 */
                <div className='tw-w-28'>
                    <Dropdown
                        label=""
                        size="sm"
                        usePortal
                        placeholder="Select"
                        options={attributeActions}
                        value={!row?.checked || !row?.action ? undefined : row.action}
                        onChange={(value) => handleActionChange(row.attribute_id, value as AttributeActionType)}
                        disabled={!row?.checked}
                        data-testid={`recategorize-source-action-${row.attribute_id}`}
                    />
                </div>
            ),
        },
        {
            id: 'target',
            header: payload?.target_category_name ?? '',
            accessor: 'target_attribute_name',
            width: '35%',
            cellType: 'dropdown',
            render: (_v, row, index) => (
                row?.action == null || !row?.checked ? <div className='tw-w-44'>NA</div> : row?.action === 'map' ? (
                    <div title={row?.target_attribute_name ?? ''} className="tw-delay-0 tw-duration-0 tw-w-44">
                        <Select<OptionType>
                            {...selectProps}
                            options={targetAttributeOptions(
                                row.attribute_id,
                                recategorizationResponse?.unique_attr_target_list ?? [],
                                payload?.unique_attr_source_list.filter((e) =>
                                    e?.action === 'map' && e.target_attribute_id != null
                                )
                            )}
                            onChange={(value) => handleTargetAttributeChange(
                                row.attribute_id,
                                value?.value ?? index.toString(),
                                value?.label ?? index.toString()
                            )}
                            value={row?.target_attribute_id != null && row?.target_attribute_name != null
                                ? { "label": row.target_attribute_name, "value": row?.target_attribute_id }
                                : undefined
                            }
                            components={{
                                Option: (props) => (
                                    <components.Option {...props}>
                                        <div className="tw-truncate" title={props.children?.toString()}>
                                            {props.children}
                                        </div>
                                    </components.Option>
                                ),
                                IndicatorSeparator: () => null,
                                NoOptionsMessage: () => (
                                    <div className="tw-p-2 tw-text-center tw-text-ink-tertiary">
                                        No attribute available
                                    </div>
                                )
                            }}
                        />
                    </div>
                ) : (
                    <div className="tw-w-44">{row.attribute_name}</div>
                )
            ),
        },
    ], [checkedRows, payload, recategorizationResponse, actionTypeSelectProps, selectProps]);

    return (
        <div className="tw-w-full tw-max-w-4xl tw-mx-auto tw-mt-3">
            <Table<UniqueAttributePayload>
                columns={columns}
                data={payload.unique_attr_source_list}
                rowKey="attribute_id"
                striped
                bordered
                data-testid="recategorize-source-attributes-table"
                emptyContent={null}
                expandedRowKeys={[...expandedRows]}
                onExpandChange={(key) => toggleExpand(String(key))}
                isRowExpandable={(row) => row?.action === 'transfer' || row?.action === 'map'}
                expandLabel={(row, expanded) => `${expanded ? 'Collapse' : 'Expand'} ${row.attribute_name ?? ''}`}
                renderExpandedRow={(row) => (
                    row?.action === 'transfer' ? (
                        <div className="tw-px-6 tw-py-2">
                            {sourceAttributeValues(row?.attribute_id)?.length ? (
                                <div>
                                    <p className="tw-text-ink-tertiary tw-font-semibold tw-text-xs">Attribute Values</p>
                                    <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mt-2">
                                        {sourceAttributeValues(row?.attribute_id).map((value) => (
                                            <span key={value.id} className="tw-px-2 tw-py-1 tw-bg-surface-sunken tw-rounded-sm tw-text-xs tw-text-ink">
                                                {value.value}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="tw-text-ink-tertiary">No attribute values available</div>
                            )}
                        </div>
                    ) : (
                        <div className="tw-px-6 tw-py-4 tw-w-full">
                            <div className="tw-w-full tw-flex tw-flex-col tw-divide-y tw-divide-edge">
                                {row?.target_attribute_id != null && (
                                    <div className="tw-w-full tw-pb-4">
                                        <CommonAttributeValues attributeValues={commonAttributeValues(row?.attribute_id)} />
                                    </div>
                                )}

                                {row?.target_attribute_id != null && (
                                    <div className="tw-w-full tw-py-4">
                                        <UniqueAttributeValues
                                            attributeValues={getUniqueAttributeValues(
                                                sourceAttributeValues(row.attribute_id),
                                                getCommonValues(row.attribute_id)
                                            )}
                                            setAttributeValueData={setAttributeValueData}
                                            sourceAttributeId={row.attribute_id}
                                            targetAttributeId={row?.target_attribute_id}
                                            payload={payload}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                )}
            />
        </div>
    );
}

export default SourceUniqueAttributesTable